<template>
  <div id="app">
    <router-view v-slot="{ Component, route }">
      <template v-if="route.meta.showLayout">
        <MainLayout>
          <component :is="Component" :key="route.path" />
        </MainLayout>
      </template>
      <template v-else>
        <component :is="Component" :key="route.path" />
      </template>
    </router-view>
  </div>
</template>

<script>
import { onMounted, ref, provide } from 'vue'
import axios from 'axios'
import config from '@/config'
import MainLayout from '@/layouts/MainLayout.vue'

export default {
  name: 'App',
  components: {
    MainLayout
  },
  setup() {
    const webConfig = ref({
      shop_name: '全域AI',
      shop_logo: '',
      h5_favicon: '',
      copyright_config: []
    })

    // 提供 webConfig 给所有子组件
    provide('webConfig', webConfig)

    // 在 fetchWebConfig 方法中修改接口调用
    const fetchWebConfig = async () => {
      try {
        // 获取当前访问的域名（前端运行域名）
        const currentDomain = window.location.host;

        let apiUrl = `${config.BASE_URL}/site/api/web_icon.php`;

        // 从 URL 中获取 store_id（支持 hash 模式）
        let storeId = null;

        // 方法1：从查询参数获取（标准方式）
        const urlParams = new URLSearchParams(window.location.search);
        storeId = urlParams.get('store_id');

        // 方法2：如果查询参数中没有，尝试从 hash 中获取
        if (!storeId && window.location.hash.includes('?')) {
          const hashParams = new URLSearchParams(window.location.hash.split('?')[1]);
          storeId = hashParams.get('store_id');
        }

        if (storeId) {
          // 默认域名访问：传递 store_id 和当前域名
          apiUrl += `?store_id=${storeId}&current_domain=${encodeURIComponent(currentDomain)}`;
        } else {
          // 贴牌域名访问：只传递当前域名
          apiUrl += `?current_domain=${encodeURIComponent(currentDomain)}`;
        }

        const response = await axios.get(apiUrl);
        if (response.data.success) {
          const { shop_name, shop_logo, h5_favicon, config: copyrightConfig } = response.data.data

          // 更新 webConfig
          webConfig.value = {
            shop_name: shop_name || '全域AI',
            shop_logo: shop_logo,
            h5_favicon: h5_favicon,
            copyright_config: parseCopyrightConfig(copyrightConfig),
            source: response.data.source // 可选：记录配置来源
          }

          // 设置浏览器标题
          document.title = webConfig.value.shop_name

          // 设置浏览器图标
          const favicon = document.querySelector("link[rel*='icon']") || document.createElement('link')
          favicon.type = 'image/x-icon'
          favicon.rel = 'shortcut icon'
          favicon.href = webConfig.value.h5_favicon
          document.getElementsByTagName('head')[0].appendChild(favicon)
        }
      } catch (error) {
        console.error('获取网站配置失败:', error)
        document.title = '全域AI'
      }
    }
    // 解析版权配置
    const parseCopyrightConfig = (configString) => {
      try {
        if (!configString) return []
        const config = JSON.parse(configString)
        return Array.isArray(config) ? config : []
      } catch (error) {
        console.error('解析版权配置失败:', error)
        return []
      }
    }

    onMounted(() => {
      fetchWebConfig()
    })

    return {
      webConfig
    }
  }
}
</script>

<style>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  background: linear-gradient(90deg, #0d051a 38%, #2d1755);
  color: #e0e6ff;
  min-height: 100vh;
  overflow-x: hidden;
}

#app {
  min-height: 100vh;
  background: url('@/assets/bg.615154b6.jpg') no-repeat center center fixed;
  background-size: cover;
}

/* 全局滚动条样式 */
::-webkit-scrollbar {
  width: 8px;
}

::-webkit-scrollbar-track {
  background: rgba(110, 33, 254, 0.1);
}

::-webkit-scrollbar-thumb {
  background: linear-gradient(135deg, #6e21fe 0%, #8b5cf6 100%);
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(135deg, #5b1bd9 0%, #7c3aed 100%);
}

/* 全局 Element Plus 组件样式覆盖 */
.el-button--primary {
  background: linear-gradient(135deg, #6e21fe 0%, #8b5cf6 100%) !important;
  border: none !important;
  box-shadow: 0 4px 15px rgba(110, 33, 254, 0.3) !important;
}

.el-button--primary:hover {
  transform: translateY(-2px) !important;
  box-shadow: 0 8px 25px rgba(110, 33, 254, 0.4) !important;
}

.el-input__wrapper {
  background: rgba(255, 255, 255, 0.08) !important;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(110, 33, 254, 0.3) !important;
  border-radius: 8px !important;
}

.el-input__wrapper:hover {
  border-color: #6e21fe !important;
  box-shadow: 0 4px 15px rgba(110, 33, 254, 0.2) !important;
}

.el-input__wrapper.is-focus {
  border-color: #6e21fe !important;
  box-shadow: 0 4px 20px rgba(110, 33, 254, 0.3) !important;
}

.el-card {
  background: rgba(255, 255, 255, 0.05) !important;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(110, 33, 254, 0.2) !important;
}

.el-dialog {
  background: rgba(13, 5, 26, 0.95) !important;
  backdrop-filter: blur(20px);
  border: 1px solid rgba(110, 33, 254, 0.3) !important;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5) !important;
}

.el-dropdown-menu {
  background: rgba(13, 5, 26, 0.95) !important;
  backdrop-filter: blur(20px);
  border: 1px solid rgba(110, 33, 254, 0.3) !important;
}

.el-dropdown-menu__item {
  color: #e0e6ff !important;
}

.el-dropdown-menu__item:hover {
  background: rgba(110, 33, 254, 0.1) !important;
  color: #6e21fe !important;
}

/* 移动端优化 */
@media (max-width: 768px) {
  .el-dialog {
    width: 90% !important;
    margin: 5vh auto !important;
  }

  .el-message {
    min-width: 90% !important;
  }
}

/* 防止页面缩放 */
@media (max-width: 480px) {
  body {
    -webkit-text-size-adjust: 100%;
  }
}
</style>
